import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  Bars3Icon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import configuratorService from '../services/configuratorService';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
  margin-bottom: 24px;
  font-size: 0.875rem;

  &:hover {
    color: ${({ theme }) => theme.text};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &.primary {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
    }
  }

  &.secondary {
    background: ${({ theme }) => theme.cardBg};
    color: ${({ theme }) => theme.text};
    border: 1px solid ${({ theme }) => theme.border};

    &:hover {
      border-color: #3b82f6;
    }
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.border};
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-size: 1.125rem;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
  }
`;

const StatusList = styled.div`
  padding: 16px;
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.bgSecondary};
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.border};
  }

  &:last-child {
    margin-bottom: 0;
  }

  .drag-handle {
    cursor: grab;
    color: ${({ theme }) => theme.textSecondary};

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .color-badge {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    flex-shrink: 0;
  }

  .status-info {
    flex: 1;

    .name {
      font-weight: 500;
      color: ${({ theme }) => theme.text};
    }

    .meta {
      font-size: 0.75rem;
      color: ${({ theme }) => theme.textSecondary};
      display: flex;
      gap: 8px;
    }
  }

  .badges {
    display: flex;
    gap: 6px;
  }

  .actions {
    display: flex;
    gap: 4px;
  }
`;

const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${({ color }) => `${color}20`};
  color: ${({ color }) => color};
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.cardBg};
    color: ${({ theme }) => theme.text};
  }

  &.danger:hover {
    background: #fee2e2;
    color: #dc2626;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;

  svg {
    width: 48px;
    height: 48px;
    color: ${({ theme }) => theme.textSecondary};
    margin-bottom: 12px;
  }

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    margin-bottom: 8px;
  }

  p {
    color: ${({ theme }) => theme.textSecondary};
    margin-bottom: 20px;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border-radius: 16px;
  padding: 24px;
  max-width: 450px;
  width: 90%;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 20px;
    color: ${({ theme }) => theme.text};
  }

  .form-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid ${({ theme }) => theme.border};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;

  label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: ${({ theme }) => theme.text};
    margin-bottom: 6px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.inputBg};
  color: ${({ theme }) => theme.text};
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const ColorInputRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  input[type='color'] {
    width: 48px;
    height: 36px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    padding: 0;
  }
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 8px 0;

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  span {
    font-size: 0.875rem;
    color: ${({ theme }) => theme.text};
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 3px solid ${({ theme }) => theme.border};
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const PipelineSettings = () => {
  const navigate = useNavigate();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    loadStatuses();
  }, []);

  const loadStatuses = async () => {
    try {
      setLoading(true);
      const data = await configuratorService.getPipelineStatuses();
      setStatuses(data.statuses || []);
    } catch (error) {
      console.error('Error loading pipeline statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('This will add default pipeline statuses. Continue?')) return;
    try {
      await configuratorService.seedDefaultStatuses();
      await loadStatuses();
    } catch (error) {
      console.error('Error seeding defaults:', error);
    }
  };

  const handleSave = async (formData) => {
    try {
      if (modal?.id) {
        await configuratorService.updatePipelineStatus(modal.id, formData);
      } else {
        await configuratorService.createPipelineStatus(formData);
      }
      await loadStatuses();
      setModal(null);
    } catch (error) {
      console.error('Error saving status:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pipeline status?')) return;
    try {
      await configuratorService.deletePipelineStatus(id);
      await loadStatuses();
    } catch (error) {
      console.error('Error deleting status:', error);
    }
  };

  const handleDragEnd = async (result) => {
    // Simplified - would use react-beautiful-dnd or similar
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/configurators')}>
        <ArrowLeftIcon />
        Back to Configurators
      </BackButton>

      <PageHeader>
        <Title>Pipeline Settings</Title>
        <div style={{ display: 'flex', gap: '12px' }}>
          {statuses.length === 0 && (
            <Button className="secondary" onClick={handleSeedDefaults}>
              <SparklesIcon />
              Add Default Statuses
            </Button>
          )}
          <Button className="primary" onClick={() => setModal({})}>
            <PlusIcon />
            Add Status
          </Button>
        </div>
      </PageHeader>

      <Card>
        <CardHeader>
          <h2>Order Pipeline Statuses</h2>
        </CardHeader>
        
        {statuses.length === 0 ? (
          <EmptyState>
            <SparklesIcon />
            <h3>No Pipeline Statuses</h3>
            <p>Create pipeline statuses to track order progress through your workflow.</p>
            <Button className="primary" onClick={handleSeedDefaults}>
              <SparklesIcon />
              Add Default Statuses
            </Button>
          </EmptyState>
        ) : (
          <StatusList>
            {statuses.map((status) => (
              <StatusItem key={status.id}>
                <div className="drag-handle">
                  <Bars3Icon />
                </div>
                <div className="color-badge" style={{ background: status.color }} />
                <div className="status-info">
                  <div className="name">{status.name}</div>
                  <div className="meta">
                    {status.description && <span>{status.description}</span>}
                  </div>
                </div>
                <div className="badges">
                  {status.is_default && <Badge color="#3b82f6">Default</Badge>}
                  {status.is_final && <Badge color="#10b981">Final</Badge>}
                </div>
                <div className="actions">
                  <IconButton onClick={() => setModal(status)}>
                    Edit
                  </IconButton>
                  <IconButton className="danger" onClick={() => handleDelete(status.id)}>
                    <TrashIcon />
                  </IconButton>
                </div>
              </StatusItem>
            ))}
          </StatusList>
        )}
      </Card>

      {modal !== null && (
        <StatusModal
          status={modal.id ? modal : null}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </PageContainer>
  );
};

const StatusModal = ({ status, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: status?.name || '',
    description: status?.description || '',
    color: status?.color || '#3b82f6',
    is_default: status?.is_default ?? false,
    is_final: status?.is_final ?? false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <h3>{status ? 'Edit Status' : 'Add Status'}</h3>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <label>Status Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g., In Review"
              required
            />
          </FormGroup>
          <FormGroup>
            <label>Description</label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
              placeholder="Optional description..."
            />
          </FormGroup>
          <FormGroup>
            <label>Color</label>
            <ColorInputRow>
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData((p) => ({ ...p, color: e.target.value }))}
              />
              <Input
                value={formData.color}
                onChange={(e) => setFormData((p) => ({ ...p, color: e.target.value }))}
              />
            </ColorInputRow>
          </FormGroup>
          <FormGroup>
            <Checkbox>
              <input
                type="checkbox"
                checked={formData.is_default}
                onChange={(e) => setFormData((p) => ({ ...p, is_default: e.target.checked }))}
              />
              <span>Default status for new orders</span>
            </Checkbox>
            <Checkbox>
              <input
                type="checkbox"
                checked={formData.is_final}
                onChange={(e) => setFormData((p) => ({ ...p, is_final: e.target.checked }))}
              />
              <span>Final status (order complete)</span>
            </Checkbox>
          </FormGroup>
          <div className="form-actions">
            <Button type="button" className="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="primary">
              {status ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default PipelineSettings;
